import {
  RetailerCollectionContentType,
  type RetailerCollectionTreeNode,
} from 'domains/retailer-collections/api'
import { type TreeNode } from 'domains/retailer-collections/components/browse/utils/types'
import { RetailerCollectionOwner } from 'domains/retailer-collections/utils/types'

export type RawTreeNode = Pick<
  RetailerCollectionTreeNode,
  'name' | 'id' | 'status' | 'startDate' | 'endDate' | 'contentType'
> & {
  children: RawTreeNode[]
}

export const toTreeNode = (
  input: RawTreeNode,
  args: {
    branch?: string[]
    pathFn: (args: string[]) => string
    owner: RetailerCollectionOwner
  }
): TreeNode => {
  const { branch = [], pathFn, owner } = args
  const level = branch.length
  const keyFn =
    owner === RetailerCollectionOwner.Instacart
      ? () =>
          `${input.contentType === RetailerCollectionContentType.Products ? 's' : 'c'}${input.id}` // Products contentType always maps to a subject
      : () => input.id // use id as-is

  const currentKey = keyFn()

  branch.push(currentKey)

  return {
    key: currentKey,
    label: input.name,
    status: input.status,
    startDate: input.startDate,
    endDate: input.endDate,
    nodes: input.children?.map(v => toTreeNode(v, { ...args, branch: [...branch] })),
    contentType: input.contentType,
    path: pathFn(branch),
    level,
    owner,
  }
}

export const indexTreeByKey = (nodes: TreeNode[], accumulator = {}): Record<string, TreeNode> =>
  nodes?.reduce((acc, node) => {
    acc[node.key] = node

    if (node.nodes) {
      Object.assign(acc, indexTreeByKey(node.nodes))
    }

    return acc
  }, accumulator)
